import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Dropdown, Avatar, Modal, Form, Input, message } from 'antd';
import { 
  HomeOutlined, 
  ThunderboltOutlined, 
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  LockOutlined,
  CloudUploadOutlined,
  TeamOutlined,
  ApiOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { auth_actions } from '../../redux/modules/auth';
import { auth_api } from '../../api/auth-api';

const { Header, Content, Sider } = Layout;

export default function BasicLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [passwordForm] = Form.useForm();
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [hasPassword, setHasPassword] = useState(null);

  const handleLogout = () => {
    dispatch(auth_actions.logout());
    navigate('/auth');
  };

  // Kiểm tra user có mật khẩu hay không khi mở modal
  useEffect(() => {
    if (passwordModalVisible && user) {
      const checkPasswordStatus = async () => {
        try {
          const result = await auth_api.checkPassword();
          if (result.success) {
            setHasPassword(result.hasPassword);
          }
        } catch (error) {
          console.error('Error checking password status:', error);
          setHasPassword(false);
        }
      };
      checkPasswordStatus();
    }
  }, [passwordModalVisible, user]);

  const handlePasswordModalOpen = () => {
    setPasswordModalVisible(true);
    passwordForm.resetFields();
  };

  const handlePasswordModalClose = () => {
    setPasswordModalVisible(false);
    passwordForm.resetFields();
  };

  const handlePasswordFinish = async (values) => {
    try {
      setPasswordLoading(true);
      
      const passwordData = {
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword
      };
      
      // Nếu user có mật khẩu, thêm mật khẩu cũ
      if (hasPassword) {
        passwordData.oldPassword = values.oldPassword;
      }
      
      const result = await auth_api.updatePassword(passwordData);
      
      if (!result) {
        throw new Error('Response không hợp lệ');
      }
      
      if (result.success) {
        message.success('Cập nhật mật khẩu thành công!');
        handlePasswordModalClose();
        // Sau khi set password, user đã có mật khẩu
        if (!hasPassword) {
          setHasPassword(true);
        }
      } else {
        message.error(result.message || 'Cập nhật mật khẩu thất bại');
      }
    } catch (error) {
      console.error('Update password error:', error);
      const errorMessage = error.message || error.response?.data?.message || 'Cập nhật mật khẩu thất bại';
      message.error(errorMessage);
    } finally {
      setPasswordLoading(false);
    }
  };

  const menuItems = [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/devices',
      icon: <ApiOutlined />,
      label: 'Thiết bị',
    },
    {
      key: '/devices/debug',
      icon: <ThunderboltOutlined />,
      label: 'SoftDebug MQTT',
    },
    {
      key: '/users',
      icon: <TeamOutlined />,
      label: 'Người dùng',
    },
    {
      key: '/mqtt',
      icon: <ApiOutlined />,
      label: 'Test MQTT',
    },
    {
      key: '/inverter-models',
      icon: <ApiOutlined />,
      label: 'Model máy',
    },
    {
      key: '/firmware',
      icon: <CloudUploadOutlined />,
      label: 'Firmware',
    },
    {
      key: '/projects',
      icon: <ThunderboltOutlined />,
      label: 'Dự án',
    },
  ];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Thông tin cá nhân',
    },
    {
      key: 'change-password',
      icon: <LockOutlined />,
      label: 'Đổi mật khẩu',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Cài đặt',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Đăng xuất',
      danger: true,
    },
  ];

  const handleMenuClick = ({ key }) => {
    if (key === 'logout') {
      handleLogout();
    } else if (key === 'profile') {
      navigate('/profile');
    } else if (key === 'change-password') {
      handlePasswordModalOpen();
    } else if (key === 'settings') {
      navigate('/settings');
    }
  };

  const handleNavClick = ({ key }) => {
    navigate(key);
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        breakpoint="lg"
        collapsedWidth="0"
        style={{
          background: '#001529',
        }}
      >
        <div style={{ 
          height: '64px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: 'white',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          Tosol Inverter
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleNavClick}
        />
      </Sider>
      <Layout>
        <Header style={{ 
          padding: '0 24px', 
          background: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
            Hệ thống quản lý Inverter
          </div>
          <Dropdown
            menu={{
              items: userMenuItems,
              onClick: handleMenuClick,
            }}
            placement="bottomRight"
          >
            <Button type="text" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Avatar 
                src={user?.photoURL && user.photoURL.trim() !== '' ? user.photoURL : undefined}
                icon={!user?.photoURL || user.photoURL.trim() === '' ? <UserOutlined /> : null}
                alt={user?.displayName || user?.email || 'User'}
              />
              <span>{user?.displayName || user?.email || 'User'}</span>
            </Button>
          </Dropdown>
        </Header>
        <Content style={{ margin: '24px 16px', padding: 24, background: '#fff', minHeight: 280 }}>
          {children}
        </Content>
      </Layout>

      {/* Modal Đổi mật khẩu */}
      <Modal
        title="Đổi mật khẩu"
        open={passwordModalVisible}
        onCancel={handlePasswordModalClose}
        footer={null}
        width={500}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordFinish}
          autoComplete="off"
        >
          {hasPassword && (
            <Form.Item
              label="Mật khẩu cũ"
              name="oldPassword"
              rules={[
                { required: true, message: 'Vui lòng nhập mật khẩu cũ' }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Nhập mật khẩu cũ"
                size="large"
              />
            </Form.Item>
          )}

          <Form.Item
            label="Mật khẩu mới"
            name="newPassword"
            rules={[
              { required: true, message: 'Vui lòng nhập mật khẩu mới' },
              { min: 6, message: 'Mật khẩu phải có ít nhất 6 ký tự' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Nhập mật khẩu mới (tối thiểu 6 ký tự)"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Xác nhận mật khẩu mới"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Vui lòng xác nhận mật khẩu mới' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Mật khẩu xác nhận không khớp'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Nhập lại mật khẩu mới"
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<LockOutlined />}
              loading={passwordLoading}
              size="large"
              block
            >
              {hasPassword ? 'Đổi mật khẩu' : 'Đặt mật khẩu'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}

